import { Navbar } from "@/components/sections/Navbar";
import { Footer } from "@/components/sections/Footer";
import { SubtleWaves } from "@/components/ui/waves";
import { Shield } from "lucide-react";
import heroImage from "@/assets/hero-lifestyle-bg.jpg";
import ingredientsImage from "@/assets/ingredients-flatlay-golden-spoon.jpg";
import lifestyleMoment from "@/assets/woman-enjoying-tea.jpg";
import teaCeremony from "@/assets/womens-balance-tea-ceremony.jpg";

const OnsVerhaalPage = () => {
  return (
    <div className="overflow-x-hidden">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${heroImage})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/60 to-background/80" />
        <SubtleWaves className="opacity-40" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <h1 className="font-serif text-5xl md:text-7xl font-light text-foreground mb-6 animate-fade-in">
            Ons Verhaal
          </h1>
          <p className="text-xl md:text-2xl text-muted-foreground font-light leading-relaxed max-w-3xl mx-auto animate-fade-in">
            Van persoonlijke worsteling naar een missie: vrouwen ondersteunen bij hun reis naar hormonale harmonie met de kracht van de natuur.
          </p>
        </div>
      </section>

      {/* Origin Story */}
      <section className="py-24 bg-background">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="order-2 md:order-1">
              <h2 className="font-serif text-4xl md:text-5xl font-light text-foreground mb-8">
                Mijn Verhaal
              </h2>
              <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
                <p>
                  Er was een tijd dat ik zelf worstelde met mijn hormonen: de vermoeidheid, de pijn, het opgeblazen gevoel… en de onzekerheid die daarbij komt kijken. Ik merkte dat ik ondanks al mijn inspanningen niet afviel, hoe gezond ik ook leefde. Pas op mijn 33ste ontdekte ik dat dit niet lag aan wilskracht, maar aan een hormonale disbalans.
                </p>
                <p>
                  En ik weet dat ik niet de enige ben. Zóveel vrouwen lopen rond met klachten zonder te weten dat hun hormonen de sleutel zijn. Ze voelen zich machteloos, worden soms niet serieus genomen, en beseffen niet dat dit ook hun vruchtbaarheid kan beïnvloeden. Het kan ontzettend moeilijk zijn om zwanger te raken wanneer je hormonen uit balans zijn.
                </p>
                <p>
                  Ik wilde niet langer wachten op snelle oplossingen of pillen die alleen de symptomen onderdrukten. Ik verlangde naar iets natuurlijks, zachts en krachtigs tegelijk. Iets dat mijn lichaam begreep. Uit dat verlangen is Lunssence geboren. 🌙
                </p>
              </div>
            </div>
            <div className="order-1 md:order-2">
              <div className="relative group">
                <img 
                  src={lifestyleMoment} 
                  alt="Rust en ontspanning met Lunssence thee"
                  className="w-full h-[500px] object-cover rounded-lg shadow-luxury group-hover:scale-[1.02] transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/10 to-transparent rounded-lg" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Values */}
      <section className="py-24 bg-muted/30 relative">
        <SubtleWaves className="opacity-20" />
        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <div className="text-center mb-20">
            <h2 className="font-serif text-4xl md:text-5xl font-light text-foreground mb-8">
              Onze Missie
            </h2>
            <div className="space-y-6 text-xl text-muted-foreground font-light leading-relaxed max-w-3xl mx-auto">
              <p>
                Bij Lunssence geloven we dat ware schoonheid en welzijn beginnen bij balans. Onze missie is om vrouwen te ondersteunen in hun reis naar hormonale harmonie, met de kracht van de natuur als leidraad.
              </p>
              <p>
                Elke blend is zorgvuldig samengesteld met kruiden die al eeuwenlang bekendstaan om hun rustgevende, herstellende en balancerende eigenschappen. Met Lunssence creëer je niet zomaar een kop thee, maar een dagelijks ritueel van zelfzorg – een moment om te ademen, te vertragen en weer in contact te komen met je lichaam en ziel.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-12">
            {[
              {
                title: "Natuurlijke Kwaliteit",
                description: "Alleen de beste, natuurlijke ingrediënten maken het tot onze blends. Geen kunstmatige toevoegingen, alleen pure natuur.",
                icon: "🌿"
              },
              {
                title: "Bewuste Rituelen",
                description: "Elke theepauze is een kans om te vertragen, adem te halen en verbinding te maken met jezelf.",
                icon: "🧘‍♀️"
              },
              {
                title: "Hormonale Harmonie",
                description: "Speciaal ontwikkeld om vrouwen te ondersteunen bij hormonale balans, vruchtbaarheid en algeheel welzijn.",
                icon: "✨"
              }
            ].map((value, index) => (
              <div key={index} className="text-center group">
                <div className="text-5xl mb-6 group-hover:scale-110 transition-transform duration-300">
                  {value.icon}
                </div>
                <h3 className="font-serif text-2xl font-medium text-foreground mb-4">
                  {value.title}
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>

          {/* NVWA Certification Badge */}
          <div className="mt-16 flex justify-center">
            <div className="inline-flex items-center gap-3 px-6 py-4 bg-gold/10 rounded-full border border-gold/20">
              <Shield className="w-6 h-6 text-gold" />
              <p className="text-foreground font-medium">
                NVWA Geregistreerd - Officieel erkende productveiligheid
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Ingredients Philosophy */}
      <section className="py-24 bg-background">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <div className="relative group">
                <img 
                  src={ingredientsImage} 
                  alt="Premium natuurlijke ingrediënten"
                  className="w-full h-[500px] object-cover rounded-lg shadow-luxury group-hover:scale-[1.02] transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/10 to-transparent rounded-lg" />
              </div>
            </div>
            <div>
              <h2 className="font-serif text-4xl md:text-5xl font-light text-foreground mb-8">
                De Kracht van de Blauwe Lotus
              </h2>
              <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
                <p>
                  Een premium kruidenthee die je niet alleen drinkt, maar beleeft. Met de mystieke energie van de blauwe lotus, de rust van citroenmelisse en de liefdevolle kracht van roos.
                </p>
                <p>
                  Elke slok is een stap dichter bij balans, rust en geluk. Want jij verdient het om je lichaam weer te vertrouwen, je energie terug te vinden en te weten dat je klachten een oorzaak hebben – en dat herstel mogelijk is. 💫
                </p>
                <p>
                  Lunssence is meer dan thee. Het is een belofte aan jezelf.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Ritual */}
      <section className="py-24 bg-muted/30 relative">
        <SubtleWaves className="opacity-20" />
        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="order-2 md:order-1">
              <h2 className="font-serif text-4xl md:text-5xl font-light text-foreground mb-8">
                Het ritueel
              </h2>
              <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
                <p>
                  Bij Lunssence gaat het niet alleen om de thee zelf, maar om het 
                  complete ritueel. Het moment waarop je de tijd neemt voor jezelf, 
                  het warme water over de bladeren giet en de geur inademt.
                </p>
                <p>
                  Deze momenten van bewustzijn zijn kostbaar. Ze brengen je terug 
                  naar het hier en nu, weg van de drukte van alledag naar een 
                  oase van rust en reflectie.
                </p>
                <p>
                  Onze thee is ontworpen om deze rituelen te verrijken en je te helpen een diepere verbinding met jezelf én je lichaam te creëren.
                </p>
              </div>
            </div>
            <div className="order-1 md:order-2">
              <div className="relative group">
                <img 
                  src={teaCeremony} 
                  alt="Thee ceremonie en ritueel"
                  className="w-full h-[500px] object-cover rounded-lg shadow-luxury group-hover:scale-[1.02] transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/10 to-transparent rounded-lg" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-24 bg-background">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-serif text-4xl md:text-5xl font-light text-foreground mb-8">
            Klaar voor jouw transformatie?
          </h2>
          <p className="text-xl text-muted-foreground font-light leading-relaxed mb-12 max-w-2xl mx-auto">
            Ontdek welke blend het beste bij jou past en start je eigen ritueel van hormonale balans, rust en innerlijke kracht. Je verdient het om je weer volledig jezelf te voelen.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <a 
              href="/producten"
              className="inline-flex items-center justify-center px-8 py-4 bg-primary text-primary-foreground font-medium rounded-lg hover:bg-primary/90 transition-all duration-300 hover:scale-105 shadow-luxury"
            >
              Ontdek onze collectie
            </a>
            <a 
              href="/#mission"
              className="inline-flex items-center justify-center px-8 py-4 border border-primary text-primary font-medium rounded-lg hover:bg-primary/10 transition-all duration-300 hover:scale-105"
            >
              Terug naar home
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default OnsVerhaalPage;